/**
 * Input Component
 *
 * Versatile input component supporting text inputs and textareas.
 * Includes validation states, icons, prefix/suffix, and character counter.
 *
 * Usage:
 *   <Input
 *     label="Email"
 *     type="email"
 *     placeholder="Enter your email"
 *     error="Invalid email"
 *   />
 *
 *   <Input
 *     as="textarea"
 *     maxRows={10}
 *     showCharCount
 *     maxLength={500}
 *   />
 */

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export interface InputProps extends Omit<React.InputHTMLAttributes<InputElement>, 'prefix' | 'size'> {
  /** Render as input or textarea */
  as?: 'input' | 'textarea';
  /** Label text above input */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Helper text below input */
  helperText?: string;
  /** Icon to display before input */
  icon?: React.ReactNode;
  /** Icon to display after input */
  iconAfter?: React.ReactNode;
  /** Text prefix (e.g., "$" for currency) */
  prefix?: string;
  /** Text suffix (e.g., ".com" for domains) */
  suffix?: string;
  /** Show character counter */
  showCharCount?: boolean;
  /** Textarea: number of rows */
  rows?: number;
  /** Textarea: max rows for auto-expand */
  maxRows?: number;
  /** Textarea: allow manual resize */
  resize?: boolean;
  /** Full width input */
  fullWidth?: boolean;
  /** Input variant */
  variant?: 'default' | 'filled';
}

export const Input = forwardRef<InputElement, InputProps>(({
  as = 'input',
  label,
  error,
  helperText,
  icon,
  iconAfter,
  prefix,
  suffix,
  showCharCount = false,
  rows = 3,
  maxRows,
  resize = false,
  fullWidth = false,
  variant = 'default',
  className = '',
  disabled,
  id,
  value,
  maxLength,
  ...props
}, ref) => {
  const internalRef = useRef<InputElement>(null);
  const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;
  const [textareaHeight, setTextareaHeight] = useState<number | null>(null);

  // Generate ID for accessibility if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = !!error;

  // Auto-expanding textarea logic
  useEffect(() => {
    if (as === 'textarea' && maxRows && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const maxHeight = lineHeight * maxRows;
      const newHeight = Math.min(scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      setTextareaHeight(newHeight);
    }
  }, [value, as, maxRows]);

  // Calculate character count
  const charCount = value ? String(value).length : 0;
  const showCounter = showCharCount && maxLength;

  // Base input/textarea styles
  const inputStyles = cn(
    'w-full min-h-[44px] px-3.5 py-2.5',
    'text-[15px] text-ink',
    'rounded-btn',
    'transition-all duration-200',
    'focus:outline-none focus:ring-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    // Adjust padding for icons/prefix/suffix
    (icon || prefix) && 'pl-11',
    (iconAfter || suffix) && 'pr-11',
    // Variant styles
    variant === 'default' && cn(
      'bg-card border-0',
      hasError ? 'shadow-[inset_0_0_0_2px_rgb(var(--c-bad))]' : 'shadow-ring-line focus:shadow-ring-ink',
      'focus:ring-0',
      'placeholder:text-ink-3'
    ),
    variant === 'filled' && cn(
      'bg-ground border-0',
      hasError ? 'shadow-[inset_0_0_0_2px_rgb(var(--c-bad))]' : 'focus:shadow-ring-ink',
      'focus:ring-0',
      'placeholder:text-ink-3'
    ),
    // Textarea specific
    as === 'textarea' && cn(
      resize ? 'resize-y' : 'resize-none',
      'min-h-[80px]'
    )
  );

  const Element = as;

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className="block mb-1.5 text-small font-semibold text-ink-2"
        >
          {label}
        </label>
      )}

      {/* Input wrapper */}
      <div className={cn('relative flex items-center', fullWidth && 'w-full')}>
        {/* Icon or prefix */}
        {(icon || prefix) && (
          <span
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none flex items-center justify-center"
            aria-hidden="true"
          >
            {icon || <span className="text-sm">{prefix}</span>}
          </span>
        )}

        {/* Input or Textarea */}
        <Element
          ref={as === 'textarea' ? (textareaRef as any) : (ref as any)}
          id={inputId}
          className={inputStyles}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${inputId}-error` :
            helperText ? `${inputId}-helper` :
            showCounter ? `${inputId}-counter` :
            undefined
          }
          value={value}
          maxLength={maxLength}
          {...(as === 'textarea' ? { rows } : {})}
          {...(props as any)}
        />

        {/* Icon after or suffix */}
        {(iconAfter || suffix) && (
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none flex items-center justify-center"
            aria-hidden="true"
          >
            {iconAfter || <span className="text-sm">{suffix}</span>}
          </span>
        )}
      </div>

      {/* Helper text, error, or character counter */}
      <div className="flex justify-between items-start mt-1.5">
        {(error || helperText) && (
          <p
            id={hasError ? `${inputId}-error` : `${inputId}-helper`}
            className={cn(
              'text-sm',
              hasError ? 'text-bad' : 'text-ink-2'
            )}
            role={hasError ? 'alert' : undefined}
          >
            {error || helperText}
          </p>
        )}

        {showCounter && (
          <p
            id={`${inputId}-counter`}
            className={cn(
              'text-sm ml-auto',
              charCount > maxLength! * 0.9
                ? 'text-warn'
                : 'text-ink-2'
            )}
          >
            {charCount} / {maxLength}
          </p>
        )}
      </div>
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
